'use client';

import { type ReactNode, useEffect } from 'react';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import {
  useCookiebotStatisticsConsent,
  useCookiebotStatisticsDeclined,
} from '@/hooks/use-cookiebot-statistics-consent';

const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const posthogHost = process.env.NEXT_PUBLIC_POSTHOG_HOST;

// posthog-js persists opt-in/out under its default localStorage key as JSON
// `1` / `0`. It is read before init(), because init() itself fetches remote
// config and feature flags, and Cookiebot has not loaded yet at that point.
function hasStoredDecline(token: string): boolean {
  try {
    return window.localStorage.getItem(`__ph_opt_in_out_${token}`) === '0';
  } catch {
    return false;
  }
}

export function CSPostHogProvider({ children }: { children: ReactNode }) {
  const hasStatisticsConsent = useCookiebotStatisticsConsent();
  const hasExplicitlyDeclined = useCookiebotStatisticsDeclined();

  // Capturing is on by default and only an explicit decline in the Cookiebot
  // banner turns it off. A decline is remembered across page loads, and on
  // those loads PostHog is not initialized at all, so no request leaves the
  // browser until the user accepts.
  useEffect(() => {
    if (!posthogKey || !posthogHost) {
      console.warn('PostHog key or host not set. PostHog will not be initialized.');
      return;
    }

    if (hasExplicitlyDeclined) {
      if (posthog.__loaded) {
        // Drop the locally stored identity, then stop capturing. reset() must
        // come first: it clears the persisted consent state, so an opt-out
        // issued before it would be undone.
        posthog.reset();
        posthog.opt_out_capturing();
      }
      return;
    }

    if (!posthog.__loaded) {
      if (!hasStatisticsConsent && hasStoredDecline(posthogKey)) {
        return;
      }

      posthog.init(posthogKey, {
        api_host: posthogHost,
        person_profiles: 'identified_only',
        capture_performance: true,
        opt_out_persistence_by_default: true,
      });
    }

    if (hasStatisticsConsent) {
      posthog.opt_in_capturing();
    }
  }, [hasStatisticsConsent, hasExplicitlyDeclined]);

  if (!posthogKey || !posthogHost) {
    return <>{children}</>;
  }

  return <PostHogProvider client={posthog}>{children}</PostHogProvider>;
}
